import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Button, Form, Icon, Label, Modal } from "semantic-ui-react";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import { useMessageResources, useTextResources } from "../../language";
import PropTypes from "prop-types";
import { onSuccess, onWarning } from "../../components/shared/messages";
import { getDaysOfDatesRange, isNonEmptyArray, toServerDateFormat } from "../../components/shared/utils";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { GetWings } from "../../api/services/organization";
import { OverBooking, RoomTypes } from "../../api/services/next"
import { DatePicker } from "../../components/shared/DatePicker";
import NumericInput from "../../components/shared/NumericInput";
import AllowExternalUpdatesDropDown from "../../components/shared/AllowExternalUpdatesDropDown";
import _ from "lodash";
import DropDown from "../../components/shared/dropdown";
import WeekDaysSelect from "../../components/shared/WeekDaysSelect";
import { dateFormat } from "../../components/shared/constants";
import moment from "moment";
import { useConfirm } from "../../hooks/useConfirm";
import { SyncChannelManagerProducts } from "../../api/services/pms";
import RoomTypesDropDown from "../../components/shared/RoomTypesDropDown";

export const OverbookingCreationPopup = ({ extendedProps: { addRow } = { addRow: () => { } }, open, onCancelChanges, isUpdate, updatedOverbooking, askBeforeClose = true, initialDate }) => {
    const currentHotel = useCurrentHotel();
    const text = useTextResources();
    const message = useMessageResources();
    const confirm = useConfirm();

    const [overbooking, setOverBooking] = useState({});
    const [dates, setDates] = useState(() => [toServerDateFormat(moment()), toServerDateFormat(moment())]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [days, setDays] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [wings, setWings] = useState([]);
    const [roomTypesData, setRoomTypesData] = useState([]);

    const changeOverbookingField = useCallback((fieldName, value) => {
        setOverBooking((prevState) => ({ ...prevState, [fieldName]: value }), []);
    }, []);

    useEffect(() => {
        if (!open) {
            // Reset state when the modal is closed
            setOverBooking({});
            setDates([toServerDateFormat(moment()), toServerDateFormat(moment())]);
            setSelectedDates([]);
            setDays(0);
            setIsLoading(false);
            setWings([]);
            setRoomTypesData([]);
        }
    }, [open]);

    const setWingsIds = useCallback((value) => changeOverbookingField("WingIds", value), []);
    const setRoomTypeIds = useCallback((values) => changeOverbookingField("RoomTypeIds", values), []);
    const setOverbookingQuantity = useCallback(value => changeOverbookingField("MaxOverbookedRoomQuantity", value), [])
    const setAllowExternalUpdate = useCallback(value => changeOverbookingField("AllowExternalUpdate", value === "" ? null : value), [])

    const handleHotelChangedAndFetchWings = useCallback((value) => {
        const newOverbooking = {
            ...overbooking,
            HotelId: value,
            RoomTypeIds: [],
            RateIds: [],
        };
        setOverBooking(newOverbooking);
        if (newOverbooking.HotelId) {
            GetWings({ HotelId: newOverbooking.HotelId }).then(setWings)
        }
    }, [overbooking]);

    useEffect(() => {
        setOverBooking({});
        const defaultDate = toServerDateFormat(initialDate || moment());
        setDates([defaultDate, defaultDate]);
        setSelectedDates([]);
    }, [open, initialDate])

    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate: dates[0], endDate: dates[1] }]
    }, [dates, selectedDates])

    const getMissingFields = (overbookingObj) => {
        const missingFields = [];
        if (!overbookingObj.HotelId)
            missingFields.push(text("Hotel"));
        if (selectedDates.length === 0)
            missingFields.push(text("Dates"));
        if (!overbookingObj.MaxOverbookedRoomQuantity &&
            overbookingObj.MaxOverbookedRoomQuantity !== 0 &&
            typeof overbookingObj.AllowExternalUpdate !== "boolean")
            missingFields.push(text("MaxOverbookedQuantity"));
        if (isNonEmptyArray(overbookingObj.RoomTypeIds) || isNonEmptyArray(overbookingObj.WingIds)) {
            if (!isNonEmptyArray(overbookingObj.RoomTypeIds)) {
                missingFields.push(text("RoomTypes"));
            }
            if (!isNonEmptyArray(overbookingObj.WingIds)) {
                missingFields.push(text("Wings"));
            }
        }
        return missingFields;
    }

    useEffect(() => {
        if (updatedOverbooking && open)
            setOverBooking({ ...updatedOverbooking, isHotelLevel: !updatedOverbooking.WingIds });
        else setOverBooking({});
    }, [open, updatedOverbooking]);

    const getMinDate = () => {
        let startDates = selectedDates.map(dateRange => moment(dateRange.startDate));
        let minDate = startDates[0];
        for (let i = 0; i < startDates.length; i++) {
            if (startDates[i].isBefore(minDate))
                minDate = startDates[i];
        }
        return minDate;
    }

    const getMaxDate = () => {
        let endDates = selectedDates.map(dateRange => moment(dateRange.endDate));
        let maxDate = endDates[0];
        for (let i = 0; i < endDates.length; i++) {
            if (endDates[i].isAfter(maxDate))
                maxDate = endDates[i];
        }
        return maxDate;
    }

    const onSave = useCallback((isClose) => async () => {
        const missingFields = getMissingFields(overbooking);
        if (missingFields.length !== 0) {
            onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
            return;
        }
        try {
            setIsLoading(true);
            let dates = new Set(
               selectedDates.map(datePair => getDaysOfDatesRange(datePair.startDate, datePair.endDate))
                            .flat().map(x => toServerDateFormat(x)) // flatten results of all dates that are between start and end dates
                            .filter(date => days.indexOf(new Date(date).getDay()) !== -1) // filter out unchecked days
            );

            let overbookingSpec = {
                IsManual: true,
                AllowExternalUpdate: overbooking.AllowExternalUpdate,
                FromDate: getMinDate(),
                ToDate: getMaxDate(),
                HotelId: overbooking.HotelId,
                Lines: _.flatten(Array.from(dates).map(date => {
                    if (overbooking.RoomTypeIds?.length > 0 && overbooking.WingIds?.length > 0) {
                        return _.flatten(overbooking.RoomTypeIds.map(roomTypeId => overbooking.WingIds.map(wingId => ({
                            Date: new Date(date),
                            RoomTypeId: roomTypeId,
                            WingId: wingId,
                            HotelId: overbooking.HotelId,
                            AllowExternalUpdate: overbooking.AllowExternalUpdate,
                            Overbooking: overbooking.MaxOverbookedRoomQuantity ?? null
                        }))))
                    } else {
                        return [{
                            Date: new Date(date),
                            HotelId: overbooking.HotelId,
                            AllowExternalUpdate: overbooking.AllowExternalUpdate,
                            Overbooking: overbooking.MaxOverbookedRoomQuantity ?? null
                        }];
                    }
                }))
            }

            await OverBooking.UpdateOverBookings(overbookingSpec);

            let roomTypeIds = overbooking.RoomTypeIds;
            if (!overbooking.RoomTypeIds?.length) {
                roomTypeIds = await OverBooking.GetRoomTypesAffectedByHotelLevel({
                    HotelId: overbooking.HotelId,
                    FromDate: moment(getMinDate()).startOf('day').toISOString(),
                    ToDate: moment(getMaxDate()).startOf('day').toISOString()
                })
            }
            if (roomTypeIds?.length) {
                await SyncChannelManagerProducts({
                    HotelID: overbooking.HotelId,
                    RoomTypeIds: roomTypeIds,
                    StartDate: moment(getMinDate()).startOf('day').toISOString(),
                    EndDate: moment(getMaxDate()).startOf('day').toISOString(),
                    IsUpdateAvailabilty: true,
                    RecalculateAvailabilty: true,
                    Reason: `Overbooking created.`,
                    TriggerOperation: "Overbooking Update"
                })
            }

            await addRow();
            onSuccess();
            if (isClose) onCancelChanges?.();
        } catch (error) {
            onWarning(message(error?.error?.detail));
        } finally {
            setIsLoading(false);
        }
    }, [overbooking, getMissingFields])

    const onClose = async () => {
        if (askBeforeClose) {
            confirm({
                content: message('WouldYouLikeToContinue'),
                confirmButton: {
                    content: text('Yes'),
                    positive: true,
                },
                cancelButton: {
                    content: text('No'),
                    negative: true,
                },
                onConfirm: () => {
                    onCancelChanges?.();
                }
            })
        } else {
            onCancelChanges?.();
        }
    }


    useEffect(() => {
        (async () => {
            if (overbooking) {
                var data = await RoomTypes.GetRoomTypes({ HotelId: overbooking.HotelId, RateIds: overbooking.RateIds });
                setRoomTypesData(data);
            }
        })()
    }, [overbooking]);

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue);
        } else {
            const defaultDate = toServerDateFormat(initialDate || moment());
            setDates([defaultDate, defaultDate]);
        }
    }

    const addDateToList = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(dates[0], 'day') && moment(d.endDate).isSame(dates[1], 'day'))) {
            onWarning(text('WarningDuplicateDates'))
            return
        }
        setSelectedDates([
            ...selectedDates,
            {
                startDate: dates[0],
                endDate: dates[1]
            }
        ])
    }

    const hotelsSpec = useMemo(() => ({ HotelChainId: currentHotel?.HotelChainId }), [currentHotel]);
    return (
        <Modal size="small" open={open} onCancelChanges={onCancelChanges}>
            <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {`${isUpdate ? text("Update") : text("Create")} ${text("Overbooking")}`}
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                        {selectedDates.map((selectedDate, index) => (
                            <Label.Group key={index}>
                                <Label as="a">
                                    {moment(selectedDate.startDate).format(dateFormat)}
                                    -
                                    {moment(selectedDate.endDate).format(dateFormat)}
                                    <Icon key={index} name={"delete"} onClick={() => setSelectedDates(selectedDates.filter((dateRange, i) => i !== index))} />
                                </Label>
                            </Label.Group>
                        ))}
                    </Form.Group>
                    <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
                        <Form.Field width={12} style={{ display: 'grid' }}>
                            <DatePicker
                                picktype='range'
                                label={text("DateRange")}
                                onChange={handleDatesChanged}
                                clearable={false}
                                value={dates}
                            />
                        </Form.Field>
                        <Form.Field width={4}>
                            <Button primary onClick={addDateToList}>{text("AddDate")}</Button>
                        </Form.Field>
                    </Form.Group>
                    <Form.Group style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                        <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates} />
                    </Form.Group>
                    <Form.Field
                        fluid
                        variant="outlined"
                        label={text("Hotel")}
                        required
                        disabled={isUpdate}
                        control={HotelsDropDown}
                        onChange={handleHotelChangedAndFetchWings}
                        value={overbooking.HotelId}
                        spec={hotelsSpec}
                        search
                    />
                    <Form.Field
                        label={text("RoomType")}
                        placeholder={text("Select")}
                        control={RoomTypesDropDown}
                        textField="Name"
                        valueField="Id"
                        value={overbooking.RoomTypeIds}
                        multiple
                        clearable
                        onChange={setRoomTypeIds}
                        data={roomTypesData}
                        search
                    />
                    <Form.Field
                        fluid
                        variant="outlined"
                        label={text("Wing")}
                        disabled={isUpdate}
                        value={overbooking.WingIds}
                        control={DropDown}
                        textField="Name"
                        valueField="Id"
                        clearable
                        multiple
                        onChange={setWingsIds}
                        data={wings}
                        search
                    />
                    <Form.Group widths="equal">

                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            label={text("MaxOverbookedQuantity")}
                            control={NumericInput}
                            digits={0}
                            min={0}
                            clearable
                            onChange={setOverbookingQuantity}
                            value={overbooking.MaxOverbookedRoomQuantity}
                            buttonPlacement="right"
                            icon="search"
                            nullable
                        />
                        <Form.Field
                            label={text("UpdateFromExternalChannel")}
                            control={AllowExternalUpdatesDropDown}
                            onChange={setAllowExternalUpdate}
                            value={overbooking.AllowExternalUpdate}
                            clearable
                            nullable
                        />
                    </Form.Group>
                </Form>
            </Modal.Content>
            <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button negative onClick={() => onClose()}>
                    {text('Close')}
                </Button>
                <Button positive onClick={onSave(true)} loading={isLoading}>
                    {text("Saveandclose")}
                </Button>
                <Button positive onClick={onSave()} loading={isLoading}>
                    {text("Save")}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

OverbookingCreationPopup.propTypes = {
    extendedProps: PropTypes.object,
    open: PropTypes.bool,
    onCancelChanges: PropTypes.func,
    isUpdate: PropTypes.bool,
    updatedOverbooking: PropTypes.object,
    askBeforeClose: PropTypes.bool,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
};
